/**
 * Mounted once at the app root. Watches the current route, fetches any active
 * popup ads for 'all' pages plus the current page's placement, and shows at
 * most one popup per browser session (per ad id).
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAds, Ad } from "@/hooks/useAds";
import AdPopup from "./AdPopup";

const SHOWN_KEY_PREFIX = "patakare_popup_shown_";
const SHOW_DELAY_MS = 4000;

function getPlacementForPath(pathname: string): string | null {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/listings")) return "listings";
  if (pathname.startsWith("/dashboard/profile")) return "member_profile";
  if (pathname.startsWith("/dashboard")) return "member_dashboard";
  if (pathname.startsWith("/patient")) return "patient_dashboard";
  if (pathname.startsWith("/homecare")) return "homecare";
  if (pathname.startsWith("/ambulance")) return "ambulance";
  if (pathname.startsWith("/blood-bank") || pathname.startsWith("/bloodbank")) return "bloodbank";
  if (pathname.startsWith("/health-camp") || pathname.startsWith("/healthcamps")) return "healthcamp";
  return null;
}

function hasBeenShown(adId: string): boolean {
  try {
    return sessionStorage.getItem(SHOWN_KEY_PREFIX + adId) === "1";
  } catch {
    return false;
  }
}

function markShown(adId: string): void {
  try {
    sessionStorage.setItem(SHOWN_KEY_PREFIX + adId, "1");
  } catch {
    // ignore (private browsing / storage disabled)
  }
}

const PopupAdManager = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const routePlacement = getPlacementForPath(location.pathname);

  const { data: allAds } = useAds(isAdminRoute ? "__disabled__" : "all");
  const { data: routeAds } = useAds(isAdminRoute ? "__disabled__" : (routePlacement || "all"));

  const [activePopup, setActivePopup] = useState<Ad | null>(null);

  const candidate = useMemo(() => {
    const byId = new Map<string, Ad>();
    [...(allAds || []), ...(routeAds || [])].forEach((ad) => byId.set(ad.id, ad));
    const popups = Array.from(byId.values())
      .filter((ad) => ad.ad_type === "popup" && !hasBeenShown(ad.id))
      .sort((a, b) => b.priority - a.priority);
    return popups[0] || null;
  }, [allAds, routeAds]);

  useEffect(() => {
    if (!candidate || activePopup) return;
    const timer = setTimeout(() => {
      setActivePopup(candidate);
      markShown(candidate.id);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [candidate, activePopup]);

  if (!activePopup) return null;

  return <AdPopup ad={activePopup} onClose={() => setActivePopup(null)} />;
};

export default PopupAdManager;
